import { useEffect } from 'react';
import { useFeeGuideAimCode } from '../lib/feeGuide';
import { FeeGuideEmbed } from './FeeGuideEmbed';

interface FeeGuideModalProps {
  /** Whether the modal is currently visible. */
  open: boolean;
  /** Called when the user dismisses the modal (backdrop, Escape, or close button). */
  onClose: () => void;
  /** Header shown at the top of the modal. */
  title?: string;
}

/**
 * Shared fee-guide popup. Renders nothing until `open` is true, then shows the
 * MarketApts fee-guide iframe in a modal overlay. Reused by both the FeeGuide
 * section button and the HeroSolidBG secondary CTA, so the embed logic (AIM
 * code + iframe resizer) lives in one place.
 */
export function FeeGuideModal({ open, onClose, title = 'Property Fee Guide' }: FeeGuideModalProps) {
  const hasFeeGuideAimCode = Boolean(useFeeGuideAimCode());

  // Close on Escape while the modal is open, and lock body scroll behind it.
  useEffect(() => {
    if (!open || !hasFeeGuideAimCode || typeof document === 'undefined') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, hasFeeGuideAimCode]);

  if (!open || !hasFeeGuideAimCode) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 p-4 sm:p-6 md:p-10"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-6xl rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-[var(--vibe-color-surface)] px-4 py-3 sm:px-6">
          <span className="font-heading text-xl md:text-2xl text-[var(--vibe-color-text)]">{title}</span>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="ml-4 flex h-9 w-9 items-center justify-center rounded-full text-2xl leading-none text-[var(--vibe-color-text)] transition-colors hover:bg-[var(--vibe-color-surface)]"
          >
            &times;
          </button>
        </div>

        <FeeGuideEmbed className="row container p-4 sm:p-6" />
      </div>
    </div>
  );
}

export default FeeGuideModal;
